import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { api, money } from "../lib/api";
import ProductCard from "../components/ProductCard";
import MarqueeBand from "../components/MarqueeBand";
import DropAlert from "../components/DropAlert";
import Reveal from "../components/Reveal";

const HERO_ITEMS = [
  { img: "/hero/flip-phone.png", label: "The Flip Phone", year: "EST. 2004" },
  { img: "/hero/walkman.png", label: "The Walkman", year: "EST. 1979" },
  { img: "/hero/cd-player.png", label: "The CD Player", year: "EST. 1984" },
  { img: "/hero/game-boy.png", label: "The Game Boy", year: "EST. 1989" },
  { img: "/hero/digital-watch.png", label: "The Digital Watch", year: "EST. 1991" },
];

const HERO_LINES = [
  { text: "TOUCH GRASS,", cls: "text-blue" },
  { text: "PRESS BUTTONS,", cls: "text-coral" },
  { text: "STAY RETRO.", cls: "text-charcoal" },
];

const ERAS = [
  { era: "1980s", title: "THE 80s", label: "Analog Grunge", blurb: "Tape hiss, foam pads, buttons that actually click.", bg: "bg-cream", text: "text-charcoal" },
  { era: "1990s", title: "THE 90s", label: "The Golden Era", blurb: "Peak plastic. Peak cartridges. Peak everything.", bg: "bg-yellow", text: "text-charcoal" },
  { era: "2000s", title: "Y2K", label: "Plastic Fantastic", blurb: "Flip phones, digicams, and low-res confidence.", bg: "bg-coral", text: "text-white" },
];

const LORE = [
  {
    n: "01",
    title: "The Mixtape",
    body: "Playlists are infinite, which is exactly the problem. A mixtape had ninety minutes, a pencil for rewinding, and real consequences. Someone chose every song, in order, for you. That is a love letter Spotify will never write.",
    link: "/product/p-walkman",
    cta: "Meet the Walkman",
  },
  {
    n: "02",
    title: "The Flip Phone",
    body: "The snap-shut was punctuation. Ending a call used to feel like something. Then glass rectangles made every interaction identical, infinite, and exhausting. The flip phone is back because it does three things and then leaves you alone.",
    link: "/product/p-razr",
    cta: "Meet the Razr",
  },
  {
    n: "03",
    title: "The Instant Photo",
    body: "Ten thousand photos in your camera roll, zero on your wall. The instant camera forces a decision: this moment, this frame, this print. It develops in your hand and it cannot be edited. That is the whole point.",
    link: "/product/p-polaroid",
    cta: "Meet the OneStep",
  },
  {
    n: "04",
    title: "The Pocket Arcade",
    body: "No updates, no battle pass, no account, no server shutdown. You owned the game because it was a physical object in your pocket. Two AA batteries outlasted entire console generations.",
    link: "/product/p-gameboy",
    cta: "Meet the Game Boy",
  },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [sets, setSets] = useState([]);
  const [heroIdx, setHeroIdx] = useState(0);
  const stripRef = useRef(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const imgRotate = useTransform(scrollYProgress, [0, 1], [-2, 3]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/sets").then((r) => setSets(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % HERO_ITEMS.length), 3000);
    return () => clearInterval(t);
  }, []);

  const scrollStrip = (dir) => stripRef.current?.scrollBy({ left: dir * 320, behavior: "smooth" });
  const staffPicks = products.filter((p) => p.staff_pick);
  const rareFinds = products.filter((p) => p.rare);
  const amazonPicks = products.filter((p) => p.retailer === "Amazon");
  const setTotal = (s) =>
    s.product_ids.reduce((sum, pid) => sum + (products.find((p) => p.id === pid)?.price || 0), 0);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} data-testid="hero-section" className="overflow-hidden border-b-2 border-charcoal bg-cream">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 pb-14 pt-10 sm:px-6 lg:grid-cols-12 lg:items-center lg:pt-16">
          <div className="lg:col-span-7">
            <h1 className="font-display font-extrabold uppercase leading-[1.02] tracking-tight">
              {HERO_LINES.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span
                    data-testid={`hero-line-${i}`}
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ delay: 0.15 + i * 0.18, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className={`block text-[11vw] sm:text-6xl lg:text-7xl ${line.cls}`}
                  >
                    {line.text}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="mt-6 max-w-md text-base md:text-lg text-charcoal/80"
            >
              A curated storefront of 90s and early-2000s tech your feed won't shut up about.
              We hold no stock — we find the good stuff and link you straight to it.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95, duration: 0.5 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link to="/shop" data-testid="hero-cta-shop" className="btn-chunky bg-blue px-8 py-4 text-lg text-white hover:bg-coral">
                Shop the 90s <ArrowRight className="h-5 w-5" />
              </Link>
              <button
                onClick={() => document.getElementById("starter-packs")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-cta-sets"
                className="btn-chunky bg-cream px-8 py-4 text-lg hover:bg-yellow"
              >
                Starter Packs
              </button>
            </motion.div>
          </div>
          <motion.div style={{ y: imgY, rotate: imgRotate }} className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.92, rotate: 4 }}
              animate={{ opacity: 1, scale: 1, rotate: -2 }}
              transition={{ delay: 0.5, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="card-chunky overflow-hidden border-4 bg-yellow shadow-hard-lg"
              data-testid="hero-image-tile"
            >
              <div className="relative aspect-[4/5] w-full">
                <AnimatePresence mode="popLayout">
                  <motion.img
                    key={heroIdx}
                    src={HERO_ITEMS[heroIdx].img}
                    alt={HERO_ITEMS[heroIdx].label}
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.7, ease: "easeInOut" }}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </AnimatePresence>
              </div>
              <div className="flex items-center justify-between border-t-2 border-charcoal bg-charcoal px-4 py-3 text-cream">
                <span data-testid="hero-exhibit-label" className="font-mono text-xs uppercase">Exhibit: {HERO_ITEMS[heroIdx].label}</span>
                <span className="font-mono text-xs text-mint">{HERO_ITEMS[heroIdx].year}</span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* THUMB STRIP */}
        <div className="border-t-2 border-charcoal bg-white py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="mb-4 flex items-center justify-between">
              <span className="font-mono text-xs font-bold uppercase tracking-wide">Fresh Picks / Swipe</span>
              <div className="flex gap-2">
                <button onClick={() => scrollStrip(-1)} data-testid="strip-arrow-left" aria-label="Scroll left" className="rounded-full border-2 border-charcoal bg-cream p-2 shadow-hard-sm transition-transform duration-150 hover:-translate-y-0.5 hover:bg-yellow">
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <button onClick={() => scrollStrip(1)} data-testid="strip-arrow-right" aria-label="Scroll right" className="rounded-full border-2 border-charcoal bg-cream p-2 shadow-hard-sm transition-transform duration-150 hover:-translate-y-0.5 hover:bg-yellow">
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div ref={stripRef} data-testid="product-strip" className="no-scrollbar flex snap-x gap-4 overflow-x-auto pb-2">
              {products.map((p) => (
                <Link key={p.id} to={`/product/${p.id}`} data-testid={`strip-item-${p.id}`} className="group w-40 shrink-0 snap-start">
                  <div className="overflow-hidden rounded-2xl border-2 border-charcoal shadow-hard-sm transition-transform duration-150 group-hover:-translate-y-1" style={{ backgroundColor: p.tile_color }}>
                    <img src={p.image} alt={p.name} loading="lazy" className="aspect-square w-full object-cover" />
                  </div>
                  <p className="mt-2 truncate font-display text-xs font-bold uppercase">{p.name}</p>
                  <p className="font-mono text-xs text-charcoal/60">{money(p.price)}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <MarqueeBand />

      {/* STARTER PACKS */}
      <section id="starter-packs" data-testid="starter-packs-section" className="border-b-2 border-charcoal bg-yellow py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <p className="font-mono text-xs font-bold uppercase tracking-wide text-charcoal/70">Starter Packs</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl">
              Every era has a kit
            </h2>
            <p className="mt-3 max-w-lg text-charcoal/80">
              Curated bundles of 2-5 pieces that belong together. Buy them one by one, from real retailers, at real prices.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {sets.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.08}>
                <Link to={`/sets/${s.id}`} data-testid={`set-card-${s.id}`} className="group block h-full">
                  <div className="card-chunky flex h-full flex-col p-6 transition-transform duration-150 group-hover:-translate-y-1.5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight">{s.name}</h3>
                        <p className="mt-1 text-sm text-charcoal/70">{s.tagline}</p>
                      </div>
                      <span className="rounded-full border-2 border-charcoal px-3 py-1 font-mono text-[10px] font-bold uppercase" style={{ backgroundColor: s.color }}>
                        {s.product_ids.length} pieces
                      </span>
                    </div>
                    <div className="mt-5 flex gap-2">
                      {s.product_ids.map((pid) => {
                        const p = products.find((x) => x.id === pid);
                        return p ? (
                          <div key={pid} className="h-16 w-16 overflow-hidden rounded-xl border-2 border-charcoal" style={{ backgroundColor: p.tile_color }}>
                            <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover" />
                          </div>
                        ) : null;
                      })}
                    </div>
                    <div className="mt-5 flex items-center justify-between border-t-2 border-dashed border-charcoal/30 pt-4">
                      <span className="font-display text-xl font-extrabold">{money(setTotal(s))} total</span>
                      <span className="flex items-center gap-1 font-display text-sm font-bold uppercase text-blue group-hover:text-coral">
                        Open the kit <ArrowUpRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SHOP BY ERA */}
      <section data-testid="shop-by-era-section" className="border-b-2 border-charcoal bg-blue py-16 text-white lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <p className="font-mono text-xs font-bold uppercase tracking-wide text-yellow">Shop by Era</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl">
              Pick your decade
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {ERAS.map((e, i) => (
              <Reveal key={e.era} delay={i * 0.08}>
                <Link
                  to={`/shop?era=${e.era}`}
                  data-testid={`era-card-${e.era}`}
                  className={`card-chunky group block p-7 ${e.bg} ${e.text} transition-transform duration-150 hover:-translate-y-1.5`}
                >
                  <span className="font-mono text-xs font-bold uppercase opacity-70">{e.label}</span>
                  <h3 className="mt-3 font-display text-4xl font-extrabold uppercase tracking-tight lg:text-5xl">{e.title}</h3>
                  <p className="mt-3 text-sm opacity-80">{e.blurb}</p>
                  <span className="mt-6 inline-flex items-center gap-2 font-display text-sm font-bold uppercase">
                    Browse {e.era} <ArrowRight className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CERTIFIED COOL */}
      <section data-testid="certified-cool-section" className="border-b-2 border-charcoal bg-mint py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="font-mono text-xs font-bold uppercase tracking-wide text-charcoal/70">Certified Cool</p>
              <h2 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl">
                Staff picks. No notes.
              </h2>
            </div>
            <Link to="/shop" data-testid="certified-shop-all" className="btn-chunky bg-cream px-6 py-3 text-sm hover:bg-coral hover:text-white">
              Shop everything <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {staffPicks.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AMAZON FINDS */}
      <section data-testid="amazon-finds-section" className="border-b-2 border-charcoal bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <p className="font-mono text-xs font-bold uppercase tracking-wide text-charcoal/70">Amazon Finds</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl">
              Add-to-cart energy
            </h2>
            <p className="mt-3 max-w-lg text-charcoal/80">
              The pieces you can grab on Amazon right now — fast shipping, easy returns, no auction sniping.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {amazonPicks.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RARE FINDS */}
      <section data-testid="rare-finds-section" className="relative overflow-hidden border-b-2 border-charcoal bg-coral py-16 text-white lg:py-24">
        <span className="pointer-events-none absolute -right-6 top-8 -rotate-12 rounded-lg border-4 border-dashed border-yellow px-6 py-3 font-display text-3xl font-extrabold uppercase text-yellow opacity-40">
          Limited
        </span>
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <p className="font-mono text-xs font-bold uppercase tracking-wide text-yellow">Rare Finds</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl">
              It's giving 1999
            </h2>
            <p className="mt-3 max-w-lg text-white/85">
              Low stock across the entire resale internet. When these listings go, they're gone. Move fast or hold L.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {rareFinds.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <ProductCard product={p} stamp />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LORE */}
      <section data-testid="lore-section" className="bg-cream py-16 lg:py-24">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <Reveal>
            <p className="font-mono text-xs font-bold uppercase tracking-wide text-charcoal/70">Lore</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl">
              Why it died. Why it's back.
            </h2>
          </Reveal>
          <div className="mt-12 flex flex-col gap-12">
            {LORE.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.05}>
                <article data-testid={`lore-chapter-${c.n}`} className="grid gap-4 border-t-2 border-charcoal pt-8 md:grid-cols-12">
                  <span className="font-mono text-sm font-bold text-coral md:col-span-2">CH.{c.n}</span>
                  <div className="md:col-span-10">
                    <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight sm:text-3xl">{c.title}</h3>
                    <p className="mt-3 max-w-2xl text-base leading-relaxed text-charcoal/80">{c.body}</p>
                    <Link to={c.link} data-testid={`lore-link-${c.n}`} className="mt-4 inline-flex items-center gap-2 font-display text-sm font-bold uppercase text-blue hover:text-coral">
                      {c.cta} <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <DropAlert />
    </div>
  );
}
